// 批次精靈步驟 4「確認座號」單張導覽的純邏輯(kanban 106957)。
//
// 版面比照國中批次上傳(chaiwriting-j pages/batch-upload.js renderStep4):
// 一次一張大圖 + 下方縮圖列切換,取代原本「一次列出全部、每張只有 64px 縮圖」。
// PM 2026-08-04 退回的理由是縮圖被 object-cover 裁掉稿紙上緣,老師看不到手寫
// 座號就填不了必填欄位。
//
// 抽出來的都是可測的規則:何時能按下一張、按鈕文案、縮圖外框狀態。
// 畫面本身(Tailwind class)留在 BatchUpload.tsx,與國中逐項對齊。

// 座號有效 = 純數字。輸入框已即時剝掉非數字,所以「非空」等同「有效」,
// 但這裡仍照 canGoNext() 的既有規則驗一次,避免外部塞進髒值時放行。
const seatFilled = (seatNumber: string): boolean =>
  /^\d+$/.test(seatNumber.trim());

// 能否按「下一張」:座號填了、且不是最後一張(照國中 canGoNextImage)
export const canGoNextImage = (
  seatNumber: string,
  currentIndex: number,
  total: number
): boolean => seatFilled(seatNumber) && currentIndex < total - 1;

// 「下一張」的三態文案(照國中:先判斷是不是最後一張,再判斷座號)
export const nextImageLabel = (
  seatNumber: string,
  currentIndex: number,
  total: number
): string => {
  if (currentIndex >= total - 1) return "已是最後一張";
  if (!seatFilled(seatNumber)) return "請先填座號";
  return "下一張";
};

// 「上一張」的兩態文案
export const prevImageLabel = (currentIndex: number): string =>
  currentIndex === 0 ? "已是第一張" : "上一張";

/**
 * 縮圖外框狀態,三態(當前/已填座號/未填),與國中檔名判別路徑一致。
 *
 * 原本高中多一態 review(AI 辨識低信心與「接續前一位」要複查);
 * kanban 1000217 停用 AI 座號辨識後,座號來源只剩檔名判別與老師手填
 *(手填後由 cascadeSeatNumbers 往後順排),review 態失去存在前提,一併移除。
 * 逐張確認路徑的縮圖狀態(座號徽章、看過/未看過兩段式標記)不走這裡,
 * 由 BatchUpload 依 visitedIndices 判斷。
 */
export type ThumbState = "current" | "done" | "empty";

export const thumbState = (
  index: number,
  currentIndex: number,
  annotation: { seatNumber: string }
): ThumbState => {
  if (index === currentIndex) return "current";
  return seatFilled(annotation.seatNumber) ? "done" : "empty";
};

// 進入步驟 4 或圖片被刪除後,把索引夾回合法範圍(空清單回 0)
export const clampImageIndex = (index: number, total: number): number =>
  total <= 0 ? 0 : Math.min(Math.max(index, 0), total - 1);

/**
 * 讓當前縮圖保持在縮圖列的可視範圍內,回傳新的 scrollLeft;已經看得到就回 null。
 *
 * 沒有這個的話:縮圖列超過可視寬度後(桌機約 24 張、上限 40 張),連按「下一張」
 * 時 scrollLeft 一直是 0,選中框整個捲出畫面外,老師看不出自己在第幾張、
 * 也看不到哪幾張還沒填。
 *
 * 只算水平、由呼叫端直接設 scrollLeft,不用 scrollIntoView —— 後者可能連帶
 * 捲動整個頁面,把上方的大圖推出視野。
 *
 * thumbLeft 必須是「相對縮圖列內容起點」的座標(用 measureThumbLeft 取得),
 * 與 scrollLeft 同一個基準,否則兩邊比較沒有意義。
 */
export const thumbStripScrollLeft = (
  thumbLeft: number,
  thumbWidth: number,
  scrollLeft: number,
  clientWidth: number,
  gap = 8
): number | null => {
  if (thumbLeft < scrollLeft) return Math.max(0, thumbLeft - gap);
  const thumbRight = thumbLeft + thumbWidth;
  if (thumbRight > scrollLeft + clientWidth)
    return thumbRight - clientWidth + gap;
  return null;
};


/**
 * 量出縮圖相對縮圖列內容起點的左緣。
 *
 * **不能用 `thumb.offsetLeft`**(kanban 1000689 修掉的原始寫法):offsetLeft 的
 * 基準是 offsetParent,而縮圖列沒有設定 position,offsetParent 會一路往上找到
 * body —— 量到的是「距離整頁左緣」,比真實位置多出版面左側留白(QA 桌機實測
 * 104px)。
 *
 * 這個錯位往右走時被 scrollLeft 上限夾住,看起來還算正常;往左走時
 * 「thumbLeft < scrollLeft」會晚一整段才成立,當前縮圖已經完全捲出左側卻不會
 * 捲回來 —— QA 實測 scrollLeft 802 時跳到第 16 張,那張整個看不到。
 *
 * rect 相減 + scrollLeft 還原則與 offsetParent、巢狀層數都無關。
 */
export const measureThumbLeft = (
  strip: HTMLElement,
  thumb: HTMLElement
): number =>
  thumb.getBoundingClientRect().left -
  strip.getBoundingClientRect().left +
  strip.scrollLeft;
